import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';

import { IconButton, IconButtonThemes } from 'wix-ui-tpa';

import { ChevronLeft, ChevronRight } from '@wix/wix-ui-icons-common/on-stage';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { st, classes, cssStates } from './ScrollingGallery.st.css';

type ScrollingGalleryProps = {
  arrowPosition?: number | 'center';
  children: React.ReactNode;
  arialLabel?: string;
  totalItems?: number;
};

export const ScrollingGallery = ({
  arrowPosition = 'center',
  children,
  arialLabel,
  totalItems,
}: ScrollingGalleryProps) => {
  const { t } = useTranslation();

  const arrowOffset = useMemo(
    () => (arrowPosition === 'center' ? '50%' : `${arrowPosition}px`),
    [arrowPosition],
  );

  const [arrowState, setArrowState] = useState({ left: false, right: true });
  const [currentCardIndex, setCurrentCardIndex] = useState(1);
  const [announcement, setAnnouncement] = useState('');

  const scroller = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);

  const calculateCurrentCard = useCallback(() => {
    if (!scroller.current || !itemsRef.current || !totalItems) {
      return;
    }

    const { scrollLeft } = scroller.current;
    const items = itemsRef.current.children;

    if (items.length === 0) {
      return;
    }

    const itemWidth = items[0].getBoundingClientRect().width;
    const scrollIndex = Math.round(scrollLeft / itemWidth);
    const newIndex = Math.min(scrollIndex + 1, totalItems);

    if (newIndex !== currentCardIndex) {
      setCurrentCardIndex(newIndex);
      setAnnouncement(
        t('app.widget.ariallabel.cardPosition', {
          current: newIndex,
          total: totalItems,
        }),
      );
    }
  }, [totalItems, currentCardIndex, t]);

  const onClickArrow = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      switch (e.currentTarget.name) {
        case 'left': {
          scroller.current?.scrollBy({
            left: -scroller.current.offsetWidth,
            behavior: 'smooth',
          });
          break;
        }
        case 'right': {
          scroller.current?.scrollBy({
            left: scroller.current.offsetWidth,
            behavior: 'smooth',
          });
          break;
        }
      }
    },
    [],
  );

  useLayoutEffect(() => {
    const onScroll = () => {
      if (!scroller.current) {
        return;
      }

      const { scrollLeft, scrollWidth, offsetWidth } = scroller.current;

      setArrowState({
        left: scrollLeft > 0,
        right: scrollLeft + offsetWidth < scrollWidth,
      });

      calculateCurrentCard();
    };

    scroller.current?.addEventListener('scroll', onScroll);

    return () => {
      scroller.current?.removeEventListener('scroll', onScroll);
    };
  }, [calculateCurrentCard]);

  useEffect(() => {
    if (totalItems && totalItems > 0) {
      setCurrentCardIndex(1);
      setAnnouncement(
        t('app.widget.ariallabel.cardPosition', {
          current: 1,
          total: totalItems,
        }),
      );
    }
  }, [totalItems, t]);

  return (
    <div className={st(classes.root)} role="region" aria-label={arialLabel}>
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announcement}
      </div>
      <IconButton
        className={st(
          classes.arrow,
          classes.arrowLeft,
          cssStates({ visible: arrowState.left }),
        )}
        style={{ top: arrowOffset }}
        theme={IconButtonThemes.Box}
        icon={<ChevronLeft />}
        name="left"
        aria-label={t('app.widget.ariallabel.previousItem')}
        tabIndex={-1}
        onClick={onClickArrow}
      />

      <div className={st(classes.scroller)} ref={scroller}>
        <div className={st(classes.flexLayoutSizeFix)}>
          <div className={st(classes.scrollerItems)} ref={itemsRef}>
            {children}
          </div>
        </div>
      </div>

      <IconButton
        className={st(
          classes.arrow,
          classes.arrowRight,
          cssStates({ visible: arrowState.right }),
        )}
        style={{ top: arrowOffset }}
        theme={IconButtonThemes.Box}
        icon={<ChevronRight />}
        name="right"
        aria-label={t('app.widget.ariallabel.nextItem')}
        tabIndex={0}
        onClick={onClickArrow}
      />
    </div>
  );
};
